/* app/onboarding/OnboardingProvider.jsx — состояние игрового онбординга.
   Стейт-машина квеста (active + индекс шага) и авто-старт для нового пользователя.
   Источник правды «нужно ли показывать тур» — серверный флаг user.onboarding_completed
   (см. /api/auth/me/); localStorage 'restorium.onboarding_done' — страховка от
   повторного показа внутри сессии до обновления флага.
   Рендер тура — в OnboardingOverlay (монтируется в AppShell).

   Тарифные разделы: шаги с section:'analytics' пропускаются, если тариф активного
   пространства закрыл раздел (workspace.sections). Провайдер хранит КАНОНИЧЕСКИЙ индекс в
   STEPS, а наружу отдаёт позицию среди доступных шагов и их число (для полоски прогресса).
   Логика — tourSteps.js (чистая, vitest); смена пространства посреди тура безопасна.

   Режим «Продукции» (classic | касса) — тоже в gate (kitchen-ux E1c): его объявляет оболочка
   раздела событием окна, когда сервер ответил (lib/kitchenModeSignal.js); шаг «Продукции»
   отдаётся с подменой режима (tourSteps.applyKitchenMode). Не объявлен или сменилось
   пространство — classic до нового объявления. */

import React from 'react';
import { useAuth } from '../providers/AuthProvider.jsx';
import { STEPS } from './steps.jsx';
import { applyKitchenMode, indexOfStepId, resolveStepIndex, stepIndexAfter, stepPosition } from './tourSteps.js';
import { trackOnboarding } from '../../lib/api/auth.js';
import { KITCHEN_MODE_EVENT, kitchenModeOfEvent } from '../../lib/kitchenModeSignal.js';
import { applySectionCopy, isSectionEnabled } from '../../lib/planSections.js';
import { canUseAssistant } from '../../lib/assistantAccess.js';

  const { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } = React;

  export const ONB_DONE_KEY = 'restorium.onboarding_done';
  const Ctx = createContext(null);

  function isDoneLocal() {
    try { return localStorage.getItem(ONB_DONE_KEY) === '1'; } catch { return false; }
  }
  export function markDoneLocal() {
    try { localStorage.setItem(ONB_DONE_KEY, '1'); } catch { /* noop */ }
  }

  function OnboardingProvider({ children }) {
    const { user, activeWorkspace } = useAuth();
    const [active, setActive] = useState(false);
    // Канонический индекс в STEPS (не позиция среди доступных): смена пространства посреди тура
    // не сдвигает его, закрывшийся шаг просто «переезжает» на следующий доступный (resolveStepIndex).
    const [rawIndex, setRawIndex] = useState(0);
    const startedRef = useRef(false);

    // Флаги разделов — примитивами и «синтетическим» пространством: activeWorkspace получает
    // новую ссылку при каждом refreshWorkspaces, а колбэки/шаг не должны из-за этого пересоздаваться.
    const analyticsOn = isSectionEnabled(activeWorkspace, 'analytics');
    const integrationsOn = isSectionEnabled(activeWorkspace, 'integrations');
    // режим «Продукции» — из объявления её оболочки; пространство сменилось — classic до нового
    const [kitchenMode, setKitchenMode] = useState('classic');
    const wsId = activeWorkspace ? activeWorkspace.id : null;
    useEffect(() => {
      const onMode = (e) => { const mode = kitchenModeOfEvent(e); if (mode) setKitchenMode(mode); };
      window.addEventListener(KITCHEN_MODE_EVENT, onMode);
      return () => window.removeEventListener(KITCHEN_MODE_EVENT, onMode);
    }, []);
    useEffect(() => { setKitchenMode('classic'); }, [wsId]);
    const gate = useMemo(
      () => ({ sections: { analytics: analyticsOn, integrations: integrationsOn }, kitchenMode }), [analyticsOn, integrationsOn, kitchenMode]);

    // №523: квест — работа с Аргусом; участнику, которому его закрыли, тур не запускается
    // (вёл бы на скрытый пункт меню и экран «Аргус недоступен»).
    const assistantOn = canUseAssistant(activeWorkspace);
    const start = useCallback(() => {
      if (!assistantOn) return;
      setRawIndex(0); setActive(true);
    }, [assistantOn]);
    const stop = useCallback(() => { setActive(false); }, []);
    const next = useCallback(() => setRawIndex((i) => stepIndexAfter(STEPS, i, +1, gate)), [gate]);
    const back = useCallback(() => setRawIndex((i) => stepIndexAfter(STEPS, i, -1, gate)), [gate]);
    const goTo = useCallback((id) => {
      const i = indexOfStepId(STEPS, id, gate);
      if (i >= 0) { setRawIndex(i); setActive(true); }
    }, [gate]);

    // Авто-старт один раз: новый пользователь, флаг не выставлен ни на сервере, ни локально.
    useEffect(() => {
      if (startedRef.current || !user) return;
      if (user.onboarding_completed || isDoneLocal()) return;
      if (!assistantOn) return;
      startedRef.current = true;
      // лёгкая задержка — чтобы первый кадр приложения (сайдбар/чат) успел отрисоваться,
      // и подсветке было за что зацепиться.
      const id = setTimeout(() => setActive(true), 400);
      return () => clearTimeout(id);
    }, [user, assistantOn]);

    const index = resolveStepIndex(STEPS, rawIndex, gate);
    const baseStep = index >= 0 ? STEPS[index] : null;
    // текст шага для пространства с закрытым разделом (orient/finish перечисляют разделы)
    const step = useMemo(() => applyKitchenMode(applySectionCopy(baseStep, gate), gate), [baseStep, gate]);
    const { position, total } = useMemo(() => stepPosition(STEPS, rawIndex, gate), [rawIndex, gate]);

    // телеметрия воронки: вход в каждый шаг (время между шагами на сервере = время на шаге)
    useEffect(() => {
      if (!active || !step) return;
      trackOnboarding({ kind: 'step_enter', step: step.id, index: position });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, index]);

    const value = useMemo(() => ({
      active, index: position, step, total,
      start, stop, next, back, goTo,
    }), [active, position, step, total, start, stop, next, back, goTo]);

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  const useOnboarding = () => useContext(Ctx);

export { OnboardingProvider, useOnboarding };
